import * as Yup from "yup";
import { FormikHelpers } from "formik";

import { SERVICE_TYPE } from "@typings/globals";

import { buildBookingUrl } from "@services/ResolveBookingRoute";

import { TranslateFunction } from "@hooks/useTranslate";
import { useFeatureToggle } from "@hooks/useFeatureToggle";

import { FIELD, FormValues, SubmitHandlerProps } from "./ServiceSelector.types";
import { getCoverage } from "./ServiceSelector.getCoverage";

export const getValidationSchema = (
  translate: TranslateFunction,
): Yup.ObjectSchema =>
  Yup.object().shape({
    [FIELD.ZIPCODE]: Yup.string()
      .trim()
      .required(translate("batmaid_pwa_generic.validate_is_required")),
    [FIELD.COVERED]: Yup.boolean(),
  });

export const useSubmitHandler = ({
  language,
  isSSG,
  showUncoveredModal,
  showFullyBookedEotModal,
  sendEvent,
  onSuccess,
}: SubmitHandlerProps) => {
  const isEOTFullyBooked = useFeatureToggle("pwa_eot_fully_booked_modal");

  return async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>,
  ): Promise<void> => {
    const serviceType = values[FIELD.SERVICE];
    const zipCode = values[FIELD.ZIPCODE];
    const covered = values[FIELD.COVERED];

    sendEvent("service-selector-submitting", {
      context: {
        serviceType,
        zipCode,
        covered,
      },
    });

    if (isEOTFullyBooked && serviceType === SERVICE_TYPE.END_OF_TENANCY) {
      sendEvent("service-selector-eot-fully-booked", {
        context: {
          zipCode,
        },
      });

      showFullyBookedEotModal();
      return;
    }

    if (!covered) {
      const isCovered = await getCoverage({
        serviceType,
        zipCode,
        showUncoveredModal,
      });

      if (!isCovered) {
        setSubmitting(false);
        return;
      }
    }

    const url = buildBookingUrl({
      language,
      serviceType,
      zip: zipCode,
      options: {
        omitBaseRoute: !isSSG,
      },
    });

    onSuccess(url);
  };
};
